import type { Delivery } from '@citibox-panel/api-client';
import { formatDeliveryDateTime } from '../../../lib/formatDeliveryDateTime';
import { deliveryStateChipLabel } from '../../../lib/deliveryStates';
import { displayedPickupDate } from './displayedPickupDate';

// Headers written for the Zendesk ticket reader — deliberately not the on-screen overline
// column headers. The BASE set: the two columns appended after it live in the functions below,
// because one of them is conditional and the other must stay last in both lists.
//
// Two of these columns are not on the screen the paste came from, and the rule that admits both
// is the same: a column may be non-rendered only when its value is what makes the paste a
// SELF-CONTAINED RECORD. The leading «Delivery ID» is one — the ticket needs an identifier the
// row does not show — and the trailing building address is the other, since a record of a
// delivery that does not say where it was left sends the reader back to the panel. Anything that
// fails that test belongs on the screen first.
// TODO: i18n — copy en ES hasta Fase 1.
export const DELIVERY_CLIPBOARD_HEADERS = [
  'Delivery ID',
  'Fecha entrega',
  'Fecha recogida',
  'Estado',
  'Buzón',
  'Tracking',
  'Carrier',
  'Teléfono usuario',
];

// The ninth column, and the only one this list adds: «Teléfono usuario» already existed and
// merely arrived empty here. It lives apart from the shared constant on purpose — the «Usuario»
// list does not copy it, because in that flow the name sits in the ficha right above the table.
// TODO: i18n
const RECIPIENT_NAME_CLIPBOARD_HEADER = 'Nombre usuario';

// The last column, and the first that is unconditional: it exists in both lists whatever they
// hand over, so it is appended AFTER the titular branch rather than inside it — and never pushed
// onto the shared constant, where it would land before «Nombre usuario» in the location list.
//
// Qualified («de entrega»), not a bare «Dirección», for the same reason «Teléfono» became
// «Teléfono usuario»: once pasted, the table has left the panel, and in a row that also carries a
// «Usuario»'s name and phone a bare «Dirección» reads as that person's home address — which is
// exactly what it is not. It is the BUILDING's.
// TODO: i18n
const LOCATION_ADDRESS_CLIPBOARD_HEADER = 'Dirección de entrega';

// The titular of ONE row, for a list that has «Usuario» columns. Its ABSENCE (`undefined`) means
// «this list has no such columns»; a field at `null` means «it has them and this value is not on
// file». Two states that cannot be collapsed into one.
export interface ClipboardRecipient {
  phone: string | null;
  name: string | null;
}

// Paired with `deliveryClipboardValues` below: both take the same titular, so a list cannot end
// up with a header row and a value row of different widths.
export function deliveryClipboardHeaders(recipient?: ClipboardRecipient): string[] {
  return [
    ...DELIVERY_CLIPBOARD_HEADERS,
    ...(recipient === undefined ? [] : [RECIPIENT_NAME_CLIPBOARD_HEADER]),
    LOCATION_ADDRESS_CLIPBOARD_HEADER,
  ];
}

// The WYSIWYG cell values for one delivery row, in header order — the exact formatters and
// labels the row renders with, so screen and clipboard cannot drift. This is the single source
// both clipboard flavours (plain TSV + HTML table) read, so the two can never diverge.
//
// Whose phone lands in the phone column depends on the list. Without a titular it is the open
// «Usuario»'s account phone, copied verbatim and staying the account phone even on a shared (👥)
// delivery. WITH a titular it is that titular's own — the account phone is not a fallback there,
// because a building has no account and the parameter arrives empty.
export function deliveryClipboardValues(
  delivery: Delivery,
  phone: string,
  recipient?: ClipboardRecipient,
): string[] {
  const values = [
    delivery.id,
    formatDeliveryDateTime(delivery.storedDate, delivery.lockerTimezone),
    formatDeliveryDateTime(displayedPickupDate(delivery), delivery.lockerTimezone),
    deliveryStateChipLabel(delivery.status),
    delivery.boxNumber ?? '—',
    delivery.tracking ?? '—',
    delivery.carrier ?? '—',
    // The em dash, not the parameter. `recipient?.phone ?? phone` is the version that reads
    // naturally and is wrong: in a list with these columns the parameter is '', so a titular with
    // no phone on file would paste a BLANK cell while the screen shows «—». The account phone is
    // the right value only where there is no titular at all.
    recipient === undefined ? phone : (recipient.phone ?? '—'),
  ];
  return [
    ...values,
    ...(recipient === undefined ? [] : [recipient.name ?? '—']),
    delivery.locationAddress ?? '—',
  ];
}
